import { useEffect, useMemo, useState } from 'react';
import { useStatefulAsyncEffect } from 'packages/lib/utils/hooks/use-stateful-async-effect';
import { getTokenSilently, user } from 'packages/lib/auth';
import { User } from 'packages/lib/auth/data';
import { useCurrentUser } from 'packages/lib/api/annotations-ops/clients/hooks/use-user-data';
import { isActiveUser, isMotionalUser } from 'packages/lib/auth/utils';
import { PermissionRole, userHasAnyRole } from 'packages/lib/auth/permissions';

interface UseUserProfileReturns {
  loadingUserProfile: boolean;
  userProfileError: unknown;
  userProfile: User | null;
}

export const useUserProfile = (): UseUserProfileReturns => {
  const { loading, error, data } = useStatefulAsyncEffect(user, []);

  return {
    loadingUserProfile: loading,
    userProfileError: error,
    userProfile: data,
  };
};

interface UseAccessTokenReturns {
  loadingAccessToken: boolean;
  accessTokenError: unknown;
  accessToken: string | null;
}

export const useAccessToken = (): UseAccessTokenReturns => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<null | unknown>(null);
  const [data, setData] = useState<null | string>(null);
  useEffect(
    () => {
      let mounted = true;
      getTokenSilently()
        .then((res) => {
          if (!mounted) return;
          setData(res?.accessToken || '');
        })
        .catch((err) => {
          if (!mounted) return;
          setError(err);
        })
        .finally(() => {
          if (!mounted) return;
          setLoading(false);
        });
      return () => {
        mounted = false;
      };
    } /* every */
  );

  return {
    loadingAccessToken: loading,
    accessTokenError: error,
    accessToken: data,
  };
};

interface UseHasWritePermissionReturns {
  loadingHasWritePermission: boolean;
  hasWritePermissionError: unknown;
  hasWritePermission: boolean | null;
}

export const useHasWritePermission = (): UseHasWritePermissionReturns => {
  const { data: currentUser, isLoading, error } = useCurrentUser();
  const userHasPermission = useMemo(() => {
    return currentUser
      ? isMotionalUser(currentUser) ||
          userHasAnyRole(currentUser, [PermissionRole.TeamLead])
      : false;
  }, [currentUser]);
  const userIsActive = isActiveUser(currentUser);
  return {
    loadingHasWritePermission: isLoading,
    hasWritePermissionError: error,
    hasWritePermission: userHasPermission && userIsActive,
  };
};
